// src/pages/StaffManagement.jsx
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Tooltip,
  Popover,
  Badge,
  ConfigProvider,
  Tag as AntTag,
} from "antd";
import {
  listStaffUsers,
  createStaffUser,
  updateStaffUser,
  deleteStaffUser,
} from "../../api/userApi";
import "../style/theme-overrides.css";

const BRAND = {
  primary: "#FFA64D",
  primaryHover: "#FF9226",
  primaryActive: "#F5821F",
};

// Translation helper
const t = (s) => (window.translate ? window.translate(s) : s);

// Helpers for localized text stored as { en, ar }
const toText = (v) =>
  typeof v === "object" && v ? v.en || v.ar || "" : v || "";

const normalizeLoc = (v, fallback = "") => ({
  en: (v ?? fallback) || "",
  ar: (v ?? fallback) || "",
});

/**
 * Canonical UI modules mapped to backend permission modules.
 * Each feature is separated so permissions are not bundled together.
 */
const PERMISSION_MODULES = {
  dashboard: {
    en: "Dashboard",
    ar: "لوحة التحكم",
    backend: "dashboard",
    actions: ["read", "write"],
  },

  products: {
    en: "Products",
    ar: "المنتجات",
    backend: "products",
    actions: ["read", "write"],
  },

  expiredProducts: {
    en: "Expired Products",
    ar: "Expired Products",
    backend: "expire",
    actions: ["read", "write"],
  },

  barcode: {
    en: "Print Barcode",
    ar: "Print Barcode",
    backend: "barcode",
    actions: ["read", "write"],
  },

  variantAttributes: {
    en: "Variant Attributes",
    ar: "Variant Attributes",
    backend: "variantAttributes",
    actions: ["read", "write"],
  },

  catalogUploads: {
    en: "Catalog Uploads",
    ar: "Catalog Uploads",
    backend: "catalogUploads",
    actions: ["read", "write"],
  },

  brands: {
    en: "Brands",
    ar: "العلامات التجارية",
    backend: "brands",
    actions: ["read", "write"],
  },

  categories: {
    en: "Categories",
    ar: "الفئات",
    backend: "categories",
    actions: ["read", "write"],
  },

  subCategories: {
    en: "Sub Categories",
    ar: "Sub Categories",
    backend: "subCategory",
    actions: ["read", "write"],
  },

  units: {
    en: "Units",
    ar: "الوحدات",
    backend: "units",
    actions: ["read", "write"],
  },

  warehouses: {
    en: "Warehouses",
    ar: "المستودعات",
    backend: "warehouses",
    actions: ["read", "write"],
  },

  stock: {
    en: "Manage Stock",
    ar: "المخزون",
    backend: "stock",
    actions: ["read", "write"],
  },

  stockAdjustments: {
    en: "Stock Adjustments",
    ar: "Stock Adjustments",
    backend: "stockAdjustment",
    actions: ["read", "write"],
  },

  stockTransfers: {
    en: "Stock Transfers",
    ar: "Stock Transfers",
    backend: "stockTransfer",
    actions: ["read", "write"],
  },

  lowStock: {
    en: "Low Stock",
    ar: "Low Stock",
    backend: "lowStock",
    actions: ["read", "write"],
  },

  outOfStock: {
    en: "Out Of Stock",
    ar: "Out Of Stock",
    backend: "outOfStock",
    actions: ["read", "write"],
  },

  inventoryLogs: {
    en: "Inventory Logs",
    ar: "Inventory Logs",
    backend: "inventoryLogs",
    actions: ["read", "write"],
  },
  stockHistory: {
    en: "Stock History",
    ar: "Stock History",
    backend: "stockHistory",
    actions: ["read", "write"],
  },

  suppliers: {
    en: "Suppliers",
    ar: "الموردون",
    backend: "suppliers",
    actions: ["read", "write"],
  },

  purchases: {
    en: "Purchases",
    ar: "Purchases",
    backend: "purchases",
    actions: ["read", "write"],
  },
  purchaseOrderReport: {
    en: "Purchase Order Report",
    ar: "Purchase Order Report",
    backend: "purchaseOrderReport",
    actions: ["read", "write"],
  },
  customers: {
    en: "Customers",
    ar: "العملاء",
    backend: "customers",
    actions: ["read", "write"],
  },

  billers: {
    en: "Billers",
    ar: "المفوترون",
    backend: "billers",
    actions: ["read", "write"],
  },

  orders: {
    en: "Orders",
    ar: "الطلبات",
    backend: "orders",
    actions: ["read", "write"],
  },

  sales: {
    en: "Sales",
    ar: "المبيعات",
    backend: "sales",
    actions: ["read", "write"],
  },

  posSales: {
    en: "POS Sales",
    ar: "مبيعات نقاط البيع",
    backend: "pos-orders",
    actions: ["read", "write"],
  },

  onlineOrders: {
    en: "Online Orders",
    ar: "Online Orders",
    backend: "onlineOrders",
    actions: ["read", "write"],
  },

  salesReport: {
    en: "Sales Report",
    ar: "تقرير المبيعات",
    backend: "salesReport",
    actions: ["read", "write"],
  },

  purchaseReport: {
    en: "Purchase Report",
    ar: "Purchase Report",
    backend: "purchaseReport",
    actions: ["read", "write"],
  },
  inventoryReport: {
    en: "Inventory Report",
    ar: "Inventory Report",
    backend: "inventoryReport",
    actions: ["read", "write"],
  },
  soldStockReport: {
    en: "Sold Stock Report",
    ar: "Sold Stock Report",
    backend: "soldStockReport",
    actions: ["read", "write"],
  },
  invoiceReport: {
    en: "Invoice Report",
    ar: "Invoice Report",
    backend: "invoiceReport",
    actions: ["read", "write"],
  },
  supplierReport: {
    en: "Supplier Report",
    ar: "Supplier Report",
    backend: "supplierReport",
    actions: ["read", "write"],
  },
  supplierDueReport: {
    en: "Supplier Due Report",
    ar: "Supplier Due Report",
    backend: "supplierDueReport",
    actions: ["read", "write"],
  },
  customerReport: {
    en: "Customer Report",
    ar: "Customer Report",
    backend: "customerReport",
    actions: ["read", "write"],
  },
  customerDueReport: {
    en: "Customer Due Report",
    ar: "Customer Due Report",
    backend: "customerDueReport",
    actions: ["read", "write"],
  },
  productReport: {
    en: "Product Report",
    ar: "Product Report",
    backend: "productReport",
    actions: ["read", "write"],
  },
  productExpiryReport: {
    en: "Product Expiry Report",
    ar: "Product Expiry Report",
    backend: "productExpiryReport",
    actions: ["read", "write"],
  },
  productQuantityAlertReport: {
    en: "Product Quantity Alert Report",
    ar: "Product Quantity Alert Report",
    backend: "productQuantityAlertReport",
    actions: ["read", "write"],
  },
  expenseReport: {
    en: "Expense Report",
    ar: "Expense Report",
    backend: "expenseReport",
    actions: ["read", "write"],
  },
  incomeReport: {
    en: "Income Report",
    ar: "Income Report",
    backend: "incomeReport",
    actions: ["read", "write"],
  },
  taxReport: {
    en: "Tax Report",
    ar: "Tax Report",
    backend: "taxReport",
    actions: ["read", "write"],
  },
  profitLossReport: {
    en: "Profit & Loss Report",
    ar: "Profit & Loss Report",
    backend: "profitLossReport",
    actions: ["read", "write"],
  },
  annualReport: {
    en: "Annual Report",
    ar: "Annual Report",
    backend: "annualReport",
    actions: ["read", "write"],
  },
  saleTaxReport: {
    en: "Sales Tax Report",
    ar: "Sales Tax Report",
    backend: "saleTaxReport",
    actions: ["read", "write"],
  },

  invoices: {
    en: "Invoices",
    ar: "الفواتير",
    backend: "invoices",
    actions: ["read", "write"],
  },

  invoiceDetail: {
    en: "Invoice Detail",
    ar: "تفاصيل الفاتورة",
    backend: "invoiceDetail",
    actions: ["read", "write"],
  },

  transactions: {
    en: "Transactions",
    ar: "المعاملات",
    backend: "transactions",
    actions: ["read", "write"],
  },

  transactionReports: {
    en: "Transaction Reports",
    ar: "Transaction Reports",
    backend: "transactionReports",
    actions: ["read", "write"],
  },

  transactionHistory: {
    en: "Transaction History",
    ar: "Transaction History",
    backend: "transactionHistory",
    actions: ["read", "write"],
  },

  users: {
    en: "Users",
    ar: "المستخدمون",
    backend: "users",
    actions: ["read", "write", "manage"],
  },

  profile: {
    en: "Profile",
    ar: "Profile",
    backend: "profile",
    actions: ["read", "write"],
  },

  vendorLink: {
    en: "Vendor Link",
    ar: "Vendor Link",
    backend: "vendorLink",
    actions: ["read", "write"],
  },

  stores: {
    en: "Stores",
    ar: "المتاجر",
    backend: "stores",
    actions: ["read", "write"],
  },

  warranties: {
    en: "Warranties",
    ar: "الضمانات",
    backend: "warranties",
    actions: ["read", "write"],
  },

  saas: {
    en: "Subscription",
    ar: "الاشتراك",
    backend: "saas",
    actions: ["write"],
  },
};

const ROLE_OPTIONS = [
  { label: "staff", value: "staff" },
  { label: "demo", value: "demo" },
];

const GROUPS = [
  {
    titleKey: "Inventory",
    modules: [
      "products",
      "expiredProducts",
      "barcode",
      "variantAttributes",
      "catalogUploads",
      "brands",
      "categories",
      "subCategories",
      "units",
      "warehouses",
      "stock",
      "stockAdjustments",
      "stockTransfers",
      "lowStock",
      "outOfStock",
      "inventoryLogs",
      "stockHistory",
    ],
  },
  {
    titleKey: "Parties",
    modules: ["suppliers", "customers", "billers"],
  },
  {
    titleKey: "Sales & Finance",
    modules: [
      "purchases",
      "purchaseOrderReport",
      "sales",
      "posSales",
      "orders",
      "onlineOrders",
      "invoices",
      "invoiceDetail",
      "transactions",
      "transactionReports",
      "transactionHistory",
    ],
  },
  {
    titleKey: "Reports",
    modules: [
      "salesReport",
      "purchaseReport",
      "inventoryReport",
      "stockHistory",
      "soldStockReport",
      "invoiceReport",
      "supplierReport",
      "supplierDueReport",
      "customerReport",
      "customerDueReport",
      "productReport",
      "productExpiryReport",
      "productQuantityAlertReport",
      "expenseReport",
      "incomeReport",
      "taxReport",
      "profitLossReport",
      "annualReport",
      "saleTaxReport",
    ],
  },
  {
    titleKey: "Organization",
    modules: [
      "users",
      "profile",
      "vendorLink",
      "stores",
      "warranties",
      "dashboard",
      "saas",
    ],
  },
];

const BACKEND_TO_UI_MODULE = Object.fromEntries(
  Object.entries(PERMISSION_MODULES).map(([uiKey, cfg]) => [
    cfg.backend,
    uiKey,
  ]),
);

/**
 * Aliases from legacy UI/backend naming to canonical backend permission modules.
 */
const PERM_ALIAS = {
  dashboard: "dashboard",

  products: "products",
  product: "products",
  expire: "expire",
  expired: "expire",
  expiredProducts: "expire",
  barcode: "barcode",
  printBarcode: "barcode",
  variant: "variantAttributes",
  variantAttributes: "variantAttributes",
  "variant-attributes": "variantAttributes",
  uploads: "catalogUploads",
  upload: "catalogUploads",
  catalogUploads: "catalogUploads",
  "catalog-image": "catalogUploads",
  fileupload: "catalogUploads",

  brands: "brands",
  brand: "brands",

  categories: "categories",
  category: "categories",
  subCategory: "subCategory",
  subcategories: "subCategory",
  subCategories: "subCategory",
  "sub-categories": "subCategory",

  units: "units",
  unit: "units",

  warehouses: "warehouses",
  warehouse: "warehouses",

  suppliers: "suppliers",
  supplier: "suppliers",
  purchases: "purchases",
  purchase: "purchases",
  purchaseList: "purchases",
  purchaselist: "purchases",
  purchaseOrder: "purchases",
  purchaseOrders: "purchases",
  purchaseorder: "purchases",
  purchaseReturn: "purchases",
  purchaseReturns: "purchases",
  purchasereturn: "purchases",
  "purchase-list": "purchases",
  "purchase-order": "purchases",
  "purchase-return": "purchases",
  "purchase-returns": "purchases",

  purchaseorderreport: "purchaseOrderReport",
  purchaseOrderReport: "purchaseOrderReport",
  purchaseOrderReports: "purchaseOrderReport",
  purchaseReport: "purchaseReport",
  purchasereport: "purchaseReport",
  "purchase-order-report": "purchaseOrderReport",
  "purchase-report": "purchaseReport",

  customers: "customers",
  customer: "customers",

  billers: "billers",
  biller: "billers",

  stock: "stock",
  stockAdjustment: "stockAdjustment",
  stockAdjustments: "stockAdjustment",
  "stock-adjustments": "stockAdjustment",
  stockTransfer: "stockTransfer",
  stockTransfers: "stockTransfer",
  "stock-transfer": "stockTransfer",
  inventoryLogs: "inventoryLogs",
  inventoryLog: "inventoryLogs",
  log: "inventoryLogs",
  stockHistory: "stockHistory",
  stockhistory: "stockHistory",
  "stock-history": "stockHistory",
  stockHistoryReport: "stockHistory",
  "stock-history-report": "stockHistory",
  lowStock: "lowStock",
  "low-stock": "lowStock",
  outOfStock: "outOfStock",
  "out-of-stock": "outOfStock",
  stockManagement: "stock",
  manageStock: "stock",

  orders: "orders",
  salesOrder: "orders",
  salesOrders: "orders",
  onlineOrder: "onlineOrders",
  onlineOrders: "onlineOrders",
  "sales-orders": "orders",

  sales: "sales",
  sale: "sales",

  posSales: "pos-orders",
  posSale: "pos-orders",
  posOrders: "pos-orders",
  "pos-orders": "pos-orders",
  posOrder: "pos-orders",
  "pos-order": "pos-orders",

  salesReport: "salesReport",
  "sales-report": "salesReport",
  salesreport: "salesReport",
  inventoryReport: "inventoryReport",
  "inventory-report": "inventoryReport",
  inventoryreport: "inventoryReport",
  soldStockReport: "soldStockReport",
  "sold-stock-report": "soldStockReport",
  soldstockreport: "soldStockReport",
  soldstock: "soldStockReport",
  "sold-stock": "soldStockReport",
  invoiceReport: "invoiceReport",
  "invoice-report": "invoiceReport",
  invoicereport: "invoiceReport",
  invoicereportnew: "invoiceReport",
  supplierReport: "supplierReport",
  "supplier-report": "supplierReport",
  supplierreport: "supplierReport",
  supplierDueReport: "supplierDueReport",
  "supplier-due-report": "supplierDueReport",
  supplierduereport: "supplierDueReport",
  customerReport: "customerReport",
  "customer-report": "customerReport",
  customerreport: "customerReport",
  customerDueReport: "customerDueReport",
  "customer-due-report": "customerDueReport",
  customerduereport: "customerDueReport",
  productReport: "productReport",
  "product-report": "productReport",
  productreport: "productReport",
  productExpiryReport: "productExpiryReport",
  "product-expiry-report": "productExpiryReport",
  productexpiredreport: "productExpiryReport",
  productQuantityAlertReport: "productQuantityAlertReport",
  "product-quantity-report": "productQuantityAlertReport",
  productquantityreport: "productQuantityAlertReport",
  expenseReport: "expenseReport",
  "expense-report": "expenseReport",
  expensereport: "expenseReport",
  incomeReport: "incomeReport",
  "income-report": "incomeReport",
  incomereport: "incomeReport",
  taxReport: "taxReport",
  "tax-report": "taxReport",
  taxreport: "taxReport",
  profitLossReport: "profitLossReport",
  "profit-loss-report": "profitLossReport",
  profitloss: "profitLossReport",
  annualReport: "annualReport",
  "annual-report": "annualReport",
  annualreport: "annualReport",
  saleTaxReport: "saleTaxReport",
  "sales-tax": "saleTaxReport",
  salereport: "saleTaxReport",

  invoiceDetail: "invoiceDetail",
  "invoice-detail": "invoiceDetail",
  invoiceDetails: "invoiceDetail",
  invoicedetails: "invoiceDetail",

  invoices: "invoices",
  invoice: "invoices",
  salesInvoices: "invoices",

  transactions: "transactions",
  transactionHistory: "transactionHistory",
  transactionReports: "transactionReports",
  transactionReport: "transactionReports",
  "transaction-reports": "transactionReports",
  userSales: "transactionHistory",

  users: "users",
  user: "users",
  profile: "profile",
  profiles: "profile",
  vendor: "vendorLink",
  vendorLink: "vendorLink",
  "vendor-link": "vendorLink",
  "users-role": "users",
  staffManagement: "users",
  "staff-management": "users",

  stores: "stores",
  store: "stores",

  warranties: "warranties",
  warranty: "warranties",

  saas: "saas",
};

const mapModuleToPerm = (module) => PERM_ALIAS[module] || module;

const VALID_PERMISSION_CODES = new Set(
  Object.values(PERMISSION_MODULES).flatMap((cfg) =>
    cfg.actions.map((action) => `${cfg.backend}:${action}`),
  ),
);

const normalizePermCodes = (arr = []) =>
  Array.from(
    new Set(
      (Array.isArray(arr) ? arr : [])
        .map((code) => {
          const [rawModule, rawAction = "read"] = String(code).split(":");
          const moduleKey = mapModuleToPerm(rawModule);
          const action = rawAction || "read";
          return `${moduleKey}:${action}`;
        })
        .filter((code) => VALID_PERMISSION_CODES.has(code)),
    ),
  );

const ACTION_LABELS = {
  read: { en: "Read", ar: "قراءة" },
  write: { en: "Write", ar: "كتابة" },
  manage: { en: "Manage", ar: "إدارة" },
};

const getActionLabel = (action) => t(ACTION_LABELS[action]?.en || action);

const actionSortValue = (action) => {
  if (action === "read") return 1;
  if (action === "write") return 2;
  if (action === "manage") return 3;
  return 99;
};

const buildPermissionSummary = (permissions = []) => {
  const normalized = normalizePermCodes(permissions);

  const grouped = new Map();
  const counts = { read: 0, write: 0, manage: 0, other: 0 };

  normalized.forEach((code) => {
    const [backendModule, action = "read"] = String(code).split(":");

    if (!grouped.has(backendModule)) grouped.set(backendModule, new Set());
    grouped.get(backendModule).add(action);

    if (action === "read") counts.read += 1;
    else if (action === "write") counts.write += 1;
    else if (action === "manage") counts.manage += 1;
    else counts.other += 1;
  });

  const modules = Array.from(grouped.entries())
    .map(([backendModule, actionSet]) => {
      const uiKey = BACKEND_TO_UI_MODULE[backendModule];
      return {
        moduleKey: backendModule,
        moduleName: t(PERMISSION_MODULES[uiKey]?.en || backendModule),
        actions: Array.from(actionSet).sort(
          (a, b) => actionSortValue(a) - actionSortValue(b),
        ),
      };
    })
    .sort((a, b) => a.moduleName.localeCompare(b.moduleName));

  return {
    total: normalized.length,
    counts,
    modules,
  };
};

function PermissionSummaryCell({ permissions }) {
  const summary = buildPermissionSummary(permissions);

  if (!summary.total) return <span className="text-muted">—</span>;

  const previewModules = summary.modules.slice(0, 3);
  const hiddenModulesCount = Math.max(0, summary.modules.length - 3);

  const popoverContent = (
    <div className="permission-popover-content">
      <div className="permission-popover-title">
        {t("All Permissions")} ({summary.total})
      </div>
      <div className="permission-popover-list">
        {summary.modules.map((m) => (
          <div key={m.moduleKey} className="permission-popover-item">
            <span className="permission-popover-module">{m.moduleName}</span>
            <span className="permission-popover-actions">
              {m.actions.map((action) => (
                <Tag
                  key={`${m.moduleKey}:${action}`}
                  className={`permission-action-tag permission-action-${action}`}
                >
                  {getActionLabel(action)}
                </Tag>
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="permission-summary-cell">
      <div className="permission-summary-metrics">
        <span className="permission-metric permission-metric-read">
          {t("Read")} {summary.counts.read}
        </span>
        <span className="permission-metric permission-metric-write">
          {t("Write")} {summary.counts.write}
        </span>
        {summary.counts.manage > 0 && (
          <span className="permission-metric permission-metric-manage">
            {t("Manage")} {summary.counts.manage}
          </span>
        )}
      </div>

      <div className="permission-summary-tags">
        {previewModules.map((m) => (
          <Tag key={m.moduleKey} className="permission-module-tag">
            <span className="permission-module-name">{m.moduleName}</span>
            <span className="permission-module-actions">
              {m.actions
                .map((a) => getActionLabel(a).charAt(0).toUpperCase())
                .join("/")}
            </span>
          </Tag>
        ))}

        {hiddenModulesCount > 0 && (
          <Popover
            content={popoverContent}
            trigger="click"
            placement="bottomLeft"
            overlayClassName="permission-popover-overlay"
          >
            <Button type="link" size="small" className="permission-more-btn">
              {t("View All")} +{hiddenModulesCount}
            </Button>
          </Popover>
        )}
      </div>
    </div>
  );
}

PermissionSummaryCell.propTypes = {
  permissions: PropTypes.arrayOf(PropTypes.string),
};

PermissionSummaryCell.defaultProps = {
  permissions: [],
};

function Pill({ active, children, onClick, type = "read" }) {
  return (
    <AntTag.CheckableTag
      checked={active}
      onChange={onClick}
      className="mb-1"
      style={{ borderRadius: 999, padding: "4px 10px" }}
    >
      <Badge
        status={
          active
            ? type === "write"
              ? "processing"
              : type === "manage"
                ? "warning"
                : "success"
            : "default"
        }
      />
      <span
        className={`ms-1 ${
          active
            ? type === "write"
              ? "text-primary"
              : type === "manage"
                ? "text-warning"
                : "text-success"
            : ""
        }`}
      >
        {children}
      </span>
    </AntTag.CheckableTag>
  );
}

Pill.propTypes = {
  active: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["read", "write", "manage"]),
};

Pill.defaultProps = {
  type: "read",
};

function PermissionGrid({ form }) {
  const [q, setQ] = useState("");

  const getPerms = () =>
    normalizePermCodes(form.getFieldValue("permissions") || []);
  const setPerms = (arr) =>
    form.setFieldsValue({ permissions: normalizePermCodes(arr) });

  const toggle = (uiModuleKey, action) => {
    const cfg = PERMISSION_MODULES[uiModuleKey];
    if (!cfg) return;

    const backendModule = cfg.backend;
    const perms = new Set(getPerms());

    const code = `${backendModule}:${action}`;
    const readCode = `${backendModule}:read`;
    const writeCode = `${backendModule}:write`;

    if (perms.has(code)) {
      perms.delete(code);

      if (action === "read" && perms.has(writeCode)) {
        perms.delete(writeCode);
      }
    } else {
      perms.add(code);

      if (action === "write" && cfg.actions.includes("read")) {
        perms.add(readCode);
      }
    }

    setPerms([...perms]);
  };

  const removeAll = () => setPerms([]);

  const selectAll = () => {
    const all = Object.values(PERMISSION_MODULES).flatMap((cfg) => {
      const codes = [];

      if (cfg.actions.includes("read")) codes.push(`${cfg.backend}:read`);
      if (cfg.actions.includes("write")) {
        if (cfg.actions.includes("read")) codes.push(`${cfg.backend}:read`);
        codes.push(`${cfg.backend}:write`);
      }
      if (cfg.actions.includes("manage")) codes.push(`${cfg.backend}:manage`);

      return codes;
    });

    setPerms(all);
  };

  const presetViewOnly = () => {
    const view = Object.values(PERMISSION_MODULES)
      .filter((cfg) => cfg.actions.includes("read"))
      .map((cfg) => `${cfg.backend}:read`);

    setPerms(view);
  };

  const presetInventoryAdmin = () => {
    const invModules = [
      "products",
      "expiredProducts",
      "barcode",
      "variantAttributes",
      "catalogUploads",
      "brands",
      "categories",
      "subCategories",
      "units",
      "warehouses",
      "stock",
      "stockAdjustments",
      "stockTransfers",
      "lowStock",
      "outOfStock",
      "inventoryLogs",
      "stockHistory",
      "suppliers",
      "purchases",
    ];

    const codes = invModules.flatMap((m) => {
      const cfg = PERMISSION_MODULES[m];
      if (!cfg) return [];
      const out = [];
      if (cfg.actions.includes("read")) out.push(`${cfg.backend}:read`);
      if (cfg.actions.includes("write")) out.push(`${cfg.backend}:write`);
      return out;
    });

    setPerms(codes);
  };

  const presetSalesClerk = () => {
    const salesModules = [
      "sales",
      "posSales",
      "orders",
      "onlineOrders",
      "invoices",
      "invoiceDetail",
      "salesReport",
      "customers",
      "billers",
      "transactions",
      "transactionReports",
      "transactionHistory",
    ];

    const codes = salesModules.flatMap((m) => {
      const cfg = PERMISSION_MODULES[m];
      if (!cfg) return [];
      const out = [];
      if (cfg.actions.includes("read")) out.push(`${cfg.backend}:read`);
      if (cfg.actions.includes("write")) out.push(`${cfg.backend}:write`);
      return out;
    });

    setPerms(codes);
  };

  const perms = getPerms();

  const match = (uiModuleKey) => {
    if (!q.trim()) return true;
    const cfg = PERMISSION_MODULES[uiModuleKey];
    const hay =
      `${t(cfg?.en || uiModuleKey)} ${t(cfg?.hint || "")}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  };

  const countSelected = perms.length;

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: BRAND.primary,
          colorPrimaryHover: BRAND.primaryHover,
          colorPrimaryActive: BRAND.primaryActive,
        },
      }}
    >
      <div className="permission-grid">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Input
              placeholder={t("Search modules...")}
              allowClear
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 260 }}
            />

            <Tooltip title={t("View-only")}>
              <Button onClick={presetViewOnly}>{t("View-only")}</Button>
            </Tooltip>

            <Tooltip title={t("Inventory Admin")}>
              <Button onClick={presetInventoryAdmin}>
                {t("Inventory Admin")}
              </Button>
            </Tooltip>

            <Tooltip title={t("Sales Clerk")}>
              <Button onClick={presetSalesClerk}>{t("Sales Clerk")}</Button>
            </Tooltip>
          </div>

          <div className="d-flex align-items-center gap-4">
            <Badge count={countSelected} style={{ backgroundColor: "#fe9f43" }}>
              <span className="px-2">{t("Selected")}</span>
            </Badge>

            <Button onClick={removeAll}>{t("Clear")}</Button>

            <Button type="primary" ghost onClick={selectAll}>
              {t("Select All")}
            </Button>
          </div>
        </div>

        <Row gutter={[16, 16]}>
          {GROUPS.map((g) => {
            const visibleModules = g.modules.filter(match);
            if (!visibleModules.length) return null;

            return (
              <Col xs={24} md={12} key={g.titleKey}>
                <Card
                  size="small"
                  className="h-100"
                  title={<span className="fw-semibold">{t(g.titleKey)}</span>}
                  bodyStyle={{ paddingTop: 8, paddingBottom: 8 }}
                >
                  <Row gutter={[12, 12]}>
                    {visibleModules.map((uiModuleKey) => {
                      const cfg = PERMISSION_MODULES[uiModuleKey];
                      const name = t(cfg.en);
                      const backend = cfg.backend;

                      const hasRead = perms.includes(`${backend}:read`);
                      const hasWrite = perms.includes(`${backend}:write`);
                      const hasManage = perms.includes(`${backend}:manage`);

                      return (
                        <Col xs={24} key={uiModuleKey}>
                          <div className="d-flex align-items-center justify-content-between bg-light p-2 px-3 rounded-2">
                            <div>
                              <div className="fw-medium">{name}</div>
                              {cfg.hint ? (
                                <div className="text-muted small">
                                  {t(cfg.hint)}
                                </div>
                              ) : null}
                            </div>

                            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                              {cfg.actions.includes("read") && (
                                <Pill
                                  active={hasRead}
                                  onClick={() => toggle(uiModuleKey, "read")}
                                  type="read"
                                >
                                  {t("Read")}
                                </Pill>
                              )}

                              {cfg.actions.includes("write") && (
                                <Pill
                                  active={hasWrite}
                                  onClick={() => toggle(uiModuleKey, "write")}
                                  type="write"
                                >
                                  {t("Write")}
                                </Pill>
                              )}

                              {cfg.actions.includes("manage") && (
                                <Pill
                                  active={hasManage}
                                  onClick={() => toggle(uiModuleKey, "manage")}
                                  type="manage"
                                >
                                  {t("Manage")}
                                </Pill>
                              )}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    </ConfigProvider>
  );
}

PermissionGrid.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
};

export default function StaffManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form] = Form.useForm();

  const fetchRows = async () => {
    setLoading(true);
    const res = await listStaffUsers();

    if (!res.success) {
      message.error(res.error || t("Failed to load users"));
      setRows([]);
    } else {
      setRows(Array.isArray(res.data) ? res.data : []);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      username: "",
      phone: "",
      role: "staff",
      readOnly: false,
      active: true,
      permissions: [],
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      email: row.email || "",
      password: "",
      firstName: toText(row.firstName),
      lastName: toText(row.lastName),
      username: toText(row.username),
      phone: row.phone || "",
      role: row.role || "staff",
      readOnly: !!row.readOnly,
      active: !!row.active,
      permissions: normalizePermCodes(row.permissions || []),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        email: values.email.trim(),
        ...(values.password?.trim()
          ? { password: values.password.trim() }
          : {}),
        firstName: normalizeLoc(values.firstName?.trim() || ""),
        lastName: normalizeLoc(values.lastName?.trim() || ""),
        username: normalizeLoc(
          (values.username || "").trim() ||
            (values.email || "").split("@")[0] ||
            "",
        ),
        phone: values.phone?.trim() || "",
        role: values.role,
        readOnly: !!values.readOnly,
        active: !!values.active,
        permissions: normalizePermCodes(values.permissions || []),
      };

      if (!editing) {
        if (!payload.password) {
          message.warning(t("Password is required for new users"));
          setSaving(false);
          return;
        }

        const res = await createStaffUser(payload);
        if (!res.success) throw new Error(res.error || t("Create failed"));
        message.success(t("User created"));
      } else {
        const res = await updateStaffUser(editing._id, payload);
        if (!res.success) throw new Error(res.error || t("Update failed"));
        message.success(t("User updated"));
      }

      closeModal();
      await fetchRows();
    } catch (err) {
      if (!err?.errorFields) {
        message.error(err.message || t("Save failed"));
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    const res = await deleteStaffUser(id);

    if (!res.success) {
      message.error(res.error || t("Delete failed"));
    } else {
      message.success(t("User deleted"));
      fetchRows();
    }
  };

  const columns = [
    {
      title: t("Email"),
      dataIndex: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: t("Name"),
      dataIndex: "name",
      render: (_, r) => `${toText(r.firstName)} ${toText(r.lastName)}`.trim(),
      sorter: (a, b) =>
        `${toText(a.firstName)} ${toText(a.lastName)}`
          .trim()
          .localeCompare(`${toText(b.firstName)} ${toText(b.lastName)}`.trim()),
    },
    {
      title: t("Username"),
      dataIndex: "username",
      render: (_, r) => toText(r.username),
      sorter: (a, b) => toText(a.username).localeCompare(toText(b.username)),
    },
    {
      title: t("Role"),
      dataIndex: "role",
      render: (role) => <Tag>{role}</Tag>,
      sorter: (a, b) => (a.role || "").localeCompare(b.role || ""),
    },
    {
      title: t("Read Only"),
      dataIndex: "readOnly",
      render: (val) => (val ? t("Yes") : t("No")),
      sorter: (a, b) => Number(!!a.readOnly) - Number(!!b.readOnly),
    },
    {
      title: t("Active"),
      dataIndex: "active",
      render: (val) => (
        <Tag color={val ? "green" : "default"}>
          {val ? t("Active") : t("Inactive")}
        </Tag>
      ),
      sorter: (a, b) => Number(!!a.active) - Number(!!b.active),
    },
    {
      title: t("Permissions"),
      dataIndex: "permissions",
      width: 420,
      render: (perms) => <PermissionSummaryCell permissions={perms} />,
      sorter: (a, b) =>
        buildPermissionSummary(a.permissions).total -
        buildPermissionSummary(b.permissions).total,
    },
    {
      title: t("Action"),
      key: "action",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            {t("Edit")}
          </Button>
          <Popconfirm
            title={t("Delete User")}
            description={t("Are you sure you want to delete this user?")}
            okText={t("Delete")}
            cancelText={t("Cancel")}
            onConfirm={() => onDelete(record._id)}
          >
            <Button size="small" danger>
              {t("Delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>{t("Users, Roles & Permissions")}</h4>
              <h6>{t("Invite staff, edit access, and manage seats")}</h6>
            </div>
          </div>

          <div className="page-btn">
            <Link to="/profile" className="btn btn-light border me-2">
              <i className="ti ti-arrow-left me-1" />
              {t("Back to Profile")}
            </Link>

            <Button
              type="primary"
              onClick={openCreate}
              style={{ backgroundColor: "#FE9F43", borderColor: "#FE9F43" }}
            >
              <i className="ti ti-user-plus me-1" />
              {t("Add User")}
            </Button>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="search-set" />
            <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
              <span className="badge rounded-pill bg-warning-subtle text-warning border">
                {t("Seats are enforced by owner’s limit")}
              </span>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={rows}
                loading={loading}
                rowKey={(r) => r._id}
                pagination={{ pageSize: 10, showSizeChanger: true }}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={editing ? t("Edit User") : t("Add User")}
        okText={t("Save")}
        cancelText={t("Cancel")}
        confirmLoading={saving}
        onCancel={closeModal}
        onOk={onSave}
        destroyOnClose
        width={980}
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{
            email: "",
            password: "",
            firstName: "",
            lastName: "",
            username: "",
            phone: "",
            role: "staff",
            readOnly: false,
            active: true,
            permissions: [],
          }}
        >
          <Form.Item
            label={t("Email")}
            name="email"
            rules={[
              { required: true, message: t("Email is required") },
              { type: "email", message: t("Enter a valid email") },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t("Password")}
            name="password"
            tooltip={editing ? t("Leave blank to keep current") : ""}
            rules={
              editing
                ? []
                : [{ required: true, message: t("Password is required") }]
            }
          >
            <Input.Password />
          </Form.Item>

          <Space size="middle" style={{ width: "100%" }} wrap>
            <Form.Item
              label={t("First Name")}
              name="firstName"
              style={{ flex: 1, minWidth: 220 }}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t("Last Name")}
              name="lastName"
              style={{ flex: 1, minWidth: 220 }}
            >
              <Input />
            </Form.Item>
          </Space>

          <Space size="middle" style={{ width: "100%" }} wrap>
            <Form.Item
              label={t("Username")}
              name="username"
              style={{ flex: 1, minWidth: 220 }}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t("Phone")}
              name="phone"
              style={{ flex: 1, minWidth: 220 }}
            >
              <Input />
            </Form.Item>
          </Space>

          <Space size="middle" style={{ width: "100%" }} wrap>
            <Form.Item
              label={t("Role")}
              name="role"
              style={{ flex: 1, minWidth: 220 }}
              rules={[{ required: true, message: t("Role is required") }]}
            >
              <Select
                options={ROLE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.label),
                }))}
              />
            </Form.Item>

            <Form.Item
              label={t("Read Only")}
              name="readOnly"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              label={t("Active")}
              name="active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item label={t("Permissions")} name="permissions">
            <Form.Item noStyle shouldUpdate>
              {() => <PermissionGrid form={form} />}
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
